import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, createFilterOptions, InputAdornment, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react"
import { timesheetsStatuses } from "../../../../../utils/constants";
import { useTranslation } from "react-i18next";
import colors from "../../../../../scss/colors";
import classes from "./TimesheetSections.module.scss";
import { AttachFileOutlined, DeleteOutlineOutlined, ExpandMoreOutlined, PersonOutline } from "@mui/icons-material";
import { textFieldStyles } from "../../../expense/components/MainTable/helper";
import KeyValueLabel from "../../../../common/labels/KeyValueLabel/KeyValueLabel";
import StatusLabel from "../../../../common/labels/StatusLabel/StatusLabel";
import MainButton from "../../../../common/buttons/MainButton";
import CustomTable from "../../../../common/Table/CustomTable/CustomTable";
import { columns, monthly1columns, monthly2columns, monthly3columns } from '../../MainTable/helpers';
import ApproverComment from "../../ApproverComment/ApproverComment";
import { ScrollableSlider } from "../ScrollableSlider/ScrollableSlider";
import { clearMonth } from "../../../../../utils/helpers";
import { buttonSizes, buttonVariants } from "../../../../common/buttons/types";
import { submissionEnum } from "../../../../../utils/enums";

// TODO DELETE
// import PerDiemCreationComponent from "../../../../common/PerDiemCreationComponent/PerDiemCreationComponent";
// import { PerDiemInfoComponent } from "../MainTable";

const filter = createFilterOptions();

const TimesheetSections = ({
    data,
    section,
    sectionIndex,
    timesheet,
    timesheetType,
    ctx,
    timesheetInfo,
    submissionType,
    size,
    responsiveSizeTimesheet,
    handleChange,
    isTNEApprover,
    approvers,
    approver,
    setOpenPop,
    changeHandler,
    deleteHandler,
    attachmentHandler,
    startDateOfPeriod,
    isAllItemsChecked,
    mainCheckboxHandler,
    addHandler,
    allowedWorkTypes,
    deleteLine,
    rateType,
    currentMonth,
    setOpenRateError,
    saveCommentHandler,
    deleteCommentHandler,
    commentsaving,
    perdiems,
    changePerDiemHandler,
    expanded
}) => {
    const scrollableRef = useRef(null);
    const scrollablePerDiem = useRef(null);
    const [scroll, setScroll] = useState(0);
    const [openPerDiemError, setOpenPerDiemError] = useState('');
    const { t } = useTranslation();

    // Replace your two useEffects and scrollLeftRight with these:

    const scrollLeftRight = (direction) => {
        if (!scrollableRef.current) return;
        const delta = direction === 'left' ? -80 : 80;
        scrollableRef.current.scrollLeft += delta;
        // perDiem is synced by the scroll listener below
    };

    const calculateScrollFromPosition = (scrollLeft) => {
        return Math.floor(
            (scrollLeft / scrollableRef.current.scrollWidth) * ((submissionType === submissionEnum.BI_MONTHLY || submissionType === submissionEnum.BI_WEEKLY) ? 200 : 150)
        );
    };

    const scrollByArrowsKeys = (e) => {
        if (e.key === 'ArrowRight') {
            if (scroll >= 100) return;
            scrollLeftRight('right');
            setScroll(scroll + (submissionType === submissionEnum.MONTHLY ? 1.2 : 4));
        }

        if (e.key === 'ArrowLeft') {
            if (scroll <= 0) return;
            scrollLeftRight('left');
            setScroll(scroll - (submissionType === submissionEnum.MONTHLY ? 1.2 : 4));
        }
    };

    const allSectionsPerDiems = timesheet?.TimesheetSections?.map((s) => ({
        sectionId: s.Id,
        perdiems: s.TimesheetPerDiem || []
    })) || [];

    // Single effect: syncs slider ← scroll position (covers Tab, arrows, drag)
    useEffect(() => {
        const el = scrollableRef.current;
        if (!el) return;

        const handleScroll = () => {
            const maxScroll = el.scrollWidth - el.clientWidth;
            if (maxScroll === 0) return;

            const percentage = Math.round((el.scrollLeft / maxScroll) * 100);
            setScroll(percentage);

            // Keep perDiem panel in sync
            if (scrollablePerDiem.current) {
                scrollablePerDiem.current.scrollLeft = el.scrollLeft;
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, []); // no deps — ref.current is stable

    // Keyboard: drive scrollLeft directly, let the scroll listener update state
    useEffect(() => {
        const scrollByKey = (e) => {
            if (!scrollableRef.current) return;
            if (e.key === 'ArrowRight') scrollableRef.current.scrollLeft += 80;
            if (e.key === 'ArrowLeft') scrollableRef.current.scrollLeft -= 80;
        };

        window.addEventListener('keydown', scrollByKey);
        return () => window.removeEventListener('keydown', scrollByKey);
    }, []); // no `scroll` in deps — listener does the state update

    // Mouse wheel → horizontal scroll
    useEffect(() => {
        const el = scrollableRef.current;
        if (!el) return;

        let velocity = 0;
        let rafId = null;

        const glide = () => {
            if (Math.abs(velocity) < 0.5) {
                velocity = 0;
                return;
            }
            el.scrollLeft += velocity;
            if (scrollablePerDiem?.current) {
                scrollablePerDiem.current.scrollLeft = el.scrollLeft;
            }
            velocity *= 0.85; // friction — lower = stops faster, higher = glides longer
            rafId = requestAnimationFrame(glide);
        };

        const onWheel = (e) => {
            const maxScroll = el.scrollWidth - el.clientWidth;
            if (maxScroll <= 0) return;

            e.preventDefault();

            const raw = (e.deltaY !== 0 ? e.deltaY : e.deltaX) * 0.4;
            velocity += raw;

            // Cancel any in-progress glide and restart
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(glide);
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            el.removeEventListener('wheel', onWheel);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    return (
        <div key={sectionIndex} className='sectionContainer'>
            <Accordion
                expanded={timesheet?.TimesheetSections?.length > 1 ? expanded === `panel${sectionIndex}` : true}
                onChange={handleChange(`panel${sectionIndex}`)}
                sx={{
                    padding: '0',
                    borderRadius: '10px !important',
                    borderColor: timesheetsStatuses(t)?.[section?.Status?.Status?.toLowerCase()]?.color || colors.LIGHT_GREY,
                }}
                className={`${classes.desktopTableContainer} flex p-6 bg-white rounded-lg shadow-md border-l-5`}
                defaultExpanded
            >
                <AccordionSummary
                    className='flex justify-between items-center w-full gap-5'
                    sx={{
                        '&.Mui-focusVisible': {
                            backgroundColor: 'transparent',
                        }
                    }}
                    expandIcon={timesheet?.TimesheetSections?.length > 1 ? <ExpandMoreOutlined sx={{ color: colors.LIGHT_BLUE, fontSize: '40px' }} /> : null}
                    aria-controls={`panel${sectionIndex}-content`}
                    id={`panel${sectionIndex}-header`}
                >
                    <div
                        className='flex justify-between items-center w-full'

                    >
                        <div
                            className='flex gap-4'
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {!ctx.isEdit || timesheetInfo?.IsApprover
                                || [timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status) ? (
                                <div className='flex gap-2'>
                                    <PersonOutline sx={{ fontSize: '25px !important', color: `${colors.NAVY_BLUE} !important` }} />
                                    <span className='text-lg text-navy-blue'>
                                        {isTNEApprover ? `${timesheet?.User?.FirstName} ${timesheet?.User?.LastName}` : (approvers?.find((item) => item.value === section.ApproverId)?.label || approver)}
                                    </span>
                                </div>
                            ) : (
                                <Autocomplete
                                    filterOptions={(options, params) => {
                                        const filtered = filter(options, params);

                                        filtered.push({
                                            label: t('common.general.requestNewApprover'),
                                            isCustomAction: true
                                        });

                                        return filtered;
                                    }}
                                    disableClearable
                                    value={approvers?.find((item) => item.value === section.ApproverId) || approver}
                                    options={approvers || []}
                                    sx={{
                                        '& .MuiAutocomplete-root.Mui-focused': {
                                            backgroundColor: 'white',
                                        },
                                        '& .MuiAutocomplete-popupIndicator svg': {
                                            color: colors.NAVY_BLUE,
                                            fontSize: '24px'
                                        },
                                        '& .MuiFormControl-fullWidth': {
                                            width: '100%'
                                        },
                                        width: '300px',
                                        border: `1px solid ${colors.LIGHT_GREY}`,
                                        borderRadius: '10px',
                                    }}
                                    isOptionEqualToValue={(option, value) => option.value === (value?.value ?? value)}
                                    popupIcon={<ExpandMoreOutlined />}
                                    forcePopupIcon
                                    onChange={(event, newValue) => {
                                        if (newValue && newValue.isCustomAction) {
                                            setOpenPop('requestNewApprover');
                                        } else {
                                            changeHandler(section?.Id)('Approver', section.Id, newValue);
                                        }
                                    }}
                                    getOptionLabel={(option) => option.label || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={`${t('dashboard.createtimesheet.steps.selectapprover')}*`}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <PersonOutline sx={{ fontSize: '25px !important', color: `${colors.NAVY_BLUE} !important` }} />
                                                        </InputAdornment>
                                                    </>
                                                )
                                            }}
                                            sx={{
                                                ...textFieldStyles(size.width > responsiveSizeTimesheet ? '400px' : '400px'),
                                                '& input::placeholder': {
                                                    color: colors.NAVY_BLUE,
                                                    opacity: 1
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.key}
                                            className={
                                                option.isCustomAction ?
                                                    `${props.className} text-md cursor-pointer font-bold border-t border-gray-300 hover:!bg-navy-blue hover:text-white text-navy-blue`
                                                    :
                                                    `px-3 cursor-pointer text-md py-3 hover:bg-gray-100 border-l-2 border-l-transparent hover:border-l-navy-blue text-navy-blue`
                                            }
                                        >
                                            {option.label}
                                        </li>
                                    )}
                                />
                            )}

                            {((![timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status) &&
                                (!timesheetInfo.isApprover || timesheetsStatuses(t).rejected.apiStatus !== section?.Status?.Status)) ||
                                size.width <= responsiveSizeTimesheet) && (
                                    <KeyValueLabel
                                        label=""
                                        value={
                                            <StatusLabel
                                                type={(section?.Status?.Status?.toLowerCase() === 'draft' ? 'pending' : section?.Status?.Status?.toLowerCase() === 'submitted' ? 'inreview' : section?.Status?.Status?.toLowerCase()) || timesheetsStatuses(t).draft.apiStatus}
                                                text={section?.Status?.Label}
                                                isApprover={timesheetInfo.isApprover}
                                            />
                                        }
                                    />
                                )}
                        </div>
                        {(ctx.isEdit || isTNEApprover) && (
                            <div className='flex gap-5 items-center'
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                            >
                                <MainButton
                                    outlined
                                    variant={buttonVariants.PRIMARY}
                                    size={buttonSizes.XSMALL}
                                    rounded
                                    action={() => attachmentHandler(section)}
                                >
                                    <AttachFileOutlined sx={{ fontSize: '16px' }} />
                                    <span>{t('common.general.attachments')} ({section?.TimesheetAttachments?.length || 0})</span>
                                </MainButton>
                                {timesheet?.TimesheetSections?.length > 1 &&
                                    ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status) && (

                                        <MainButton
                                            outlined
                                            variant={buttonVariants.RED_PRIMARY}
                                            size={buttonSizes.XSMALL}
                                            rounded
                                            action={() => deleteHandler(section?.Id)}
                                        >
                                            <DeleteOutlineOutlined sx={{ fontSize: '16px' }} />
                                            <span>{t('common.general.deletesection')}</span>
                                        </MainButton>
                                    )}
                            </div>
                        )}
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    <div className={classes.sectionInner}>
                        {timesheetType === submissionEnum.WEEKLY ? (
                            <div className={classes.scrollableWeeklyContainer}>
                                <CustomTable
                                    withExtraFooter
                                    data={section?.TimesheetDetails || []}
                                    currentSection={section}
                                    columns={() =>
                                        columns(
                                            section?.Id,
                                            startDateOfPeriod,
                                            changeHandler(section?.Id),
                                            t,
                                            isAllItemsChecked,
                                            mainCheckboxHandler,
                                            addHandler(section?.Id),
                                            allowedWorkTypes,
                                            ctx.isEdit && ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status),
                                            deleteLine,
                                            rateType,
                                            currentMonth,
                                            timesheetType,
                                            setOpenRateError,
                                            timesheetInfo.status,
                                            timesheetInfo.user,
                                            section?.TimesheetPerDiem || [],
                                            changePerDiemHandler,
                                            !ctx.isEdit ||
                                            [timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                                                timesheetInfo.status
                                            ),
                                            allSectionsPerDiems,
                                            setOpenPerDiemError
                                        )
                                    }
                                    withFooter
                                    withLines={{
                                        header: false,
                                        body: false,
                                        footer: false
                                    }}
                                    footerMargin="0"
                                    bodyWithBorderRadius={false}
                                    headerStyles={{
                                        borderRadius: '10px 0 0 0',
                                        height: '68px',
                                        borderRight: `2px solid ${colors.DARKEST_NAVY_BLUE}`,
                                        borderLeft: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                                    }}
                                    bodyStyles={{
                                        padding: '20px 26px 0 26px',
                                        backgroundColor: colors.WHITE,
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    footerStyles={{
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    extraFooterStyles={{
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    wrapperStyles={{
                                        maxWidth: 'max-content',
                                        backgroundColor: colors.WHITE,
                                        borderRadius: '0'
                                    }}
                                />
                            </div>
                        ) : (
                            <div className={classes.auxContainer}>
                                <CustomTable
                                    withExtraFooter
                                    data={section?.TimesheetDetails || []}
                                    currentSection={section}
                                    columns={() =>
                                        monthly1columns(
                                            section?.Id,
                                            startDateOfPeriod,
                                            changeHandler(section?.Id),
                                            t,
                                            isAllItemsChecked,
                                            mainCheckboxHandler,
                                            addHandler(section?.Id),
                                            allowedWorkTypes,
                                            ctx.isEdit && ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status),
                                            deleteLine,
                                            rateType,
                                            currentMonth,
                                            timesheetType,
                                            setOpenRateError,
                                            timesheetInfo.status,
                                            timesheetInfo.user
                                        )
                                    }
                                    withFooter
                                    withLines={{
                                        header: false,
                                        body: false,
                                        footer: false
                                    }}
                                    headerStyles={{
                                        borderRadius: '10px 0 0 0',
                                        height: '68px',
                                        borderRight: `2px solid ${colors.DARKEST_NAVY_BLUE}`,
                                        borderLeft: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                                    }}
                                    bodyStyles={{
                                        padding: '20px 26px 0 26px',
                                        backgroundColor: colors.WHITE,
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    footerStyles={{
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    extraFooterStyles={{
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    wrapperStyles={{
                                        maxWidth: 'max-content',
                                        backgroundColor: colors.WHITE,
                                        borderRadius: '0'
                                    }}
                                    footerMargin="0"
                                    bodyWithBorderRadius
                                />
                                <div
                                    tabIndex={0}
                                    ref={scrollableRef}
                                    className={
                                        timesheetType === submissionEnum.MONTHLY || timesheetType === submissionEnum.WEEKLY
                                            ? classes.scrollableContainer
                                            : classes.scrollableSemiMonthlyContainer
                                    }
                                >
                                    <CustomTable
                                        withActionController={true}
                                        withExtraFooter
                                        data={timesheetType === submissionEnum.BI_WEEKLY ? (section?.TimesheetDetails || []) : clearMonth(section?.TimesheetDetails || [], currentMonth)}
                                        columns={() =>
                                            monthly2columns(
                                                section?.Id,
                                                startDateOfPeriod,
                                                changeHandler(section?.Id),
                                                t,
                                                isAllItemsChecked,
                                                mainCheckboxHandler,
                                                addHandler(section?.Id),
                                                allowedWorkTypes,
                                                ctx.isEdit && ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status),
                                                deleteLine,
                                                rateType,
                                                currentMonth,
                                                timesheetType,
                                                setOpenRateError,
                                                timesheetType === submissionEnum.BI_WEEKLY ? (section?.TimesheetDetails || []) : clearMonth(section?.TimesheetDetails || [], currentMonth),
                                                section?.TimesheetPerDiem || [],
                                                changePerDiemHandler,
                                                !ctx.isEdit ||
                                                [timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(
                                                    timesheetInfo.status
                                                ),
                                                allSectionsPerDiems,
                                                setOpenPerDiemError
                                            )
                                        }
                                        withFooter
                                        withLines={{
                                            header: false,
                                            body: false,
                                            footer: false
                                        }}
                                        headerStyles={{
                                            height: '68px',
                                            borderRadius: '0',
                                            padding: '11px 8px 0 8px'
                                        }}
                                        wrapperStyles={{
                                            backgroundColor: colors.WHITE,
                                            borderRadius: '0',
                                            boxShadow: '2px 0px 5px 0px rgba(79, 79, 79, 0.10)'
                                        }}
                                        bodyStyles={{
                                            padding: '20px 8px 0 8px',
                                            backgroundColor: colors.WHITE
                                        }}
                                        footerStyles={{
                                            padding: '0 8px 20px 8px',
                                            backgroundColor: colors.WHITE,
                                            borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        }}
                                        extraFooterStyles={{
                                            padding: '9px 0',
                                            backgroundColor: colors.WHITE,
                                            borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                        footerMargin="0"
                                        bodyWithBorderRadius={false}
                                    />
                                </div>
                                <CustomTable
                                    withExtraFooter
                                    data={section?.TimesheetDetails || []}
                                    columns={() =>
                                        monthly3columns(
                                            section?.Id,
                                            startDateOfPeriod,
                                            changeHandler(section?.Id),
                                            t,
                                            isAllItemsChecked,
                                            mainCheckboxHandler,
                                            addHandler(section?.Id),
                                            allowedWorkTypes,
                                            ctx.isEdit && ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(section?.Status?.Status),
                                            deleteLine,
                                            rateType,
                                            currentMonth,
                                            timesheetType,
                                            section?.TimesheetPerDiem || [],
                                        )
                                    }
                                    withFooter
                                    withLines={{
                                        header: false,
                                        body: false,
                                        footer: false
                                    }}
                                    footerMargin="0"
                                    headerStyles={{
                                        borderRadius: '0 10px 0 0',
                                        height: '68px',
                                        borderLeft: `2px solid ${colors.DARKEST_NAVY_BLUE}`
                                    }}
                                    bodyStyles={{
                                        padding: '20px 26px 0 26px',
                                        backgroundColor: colors.WHITE,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    footerStyles={{
                                        padding: '0 26px 20px 26px',
                                        backgroundColor: colors.WHITE,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    extraFooterStyles={{
                                        padding: '9px 0',
                                        backgroundColor: colors.WHITE,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: colors.WHITE,
                                        borderLeft: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderRight: `2px solid ${colors.PAGE_BACKGROUND}`,
                                        borderBottom: `2px solid ${colors.PAGE_BACKGROUND}`,
                                    }}
                                    wrapperStyles={{
                                        maxWidth: 'max-content',
                                        backgroundColor: colors.WHITE,
                                        borderRadius: '0 0 10px 0',
                                        boxShadow: 'none'
                                    }}
                                    bodyWithBorderRadius
                                />
                            </div>
                        )}
                        {(submissionType === submissionEnum.MONTHLY || submissionType === submissionEnum.BI_MONTHLY || submissionType === submissionEnum.BI_WEEKLY) &&
                            size.width > responsiveSizeTimesheet && (
                                <ScrollableSlider
                                    scroll={scroll}
                                    setScroll={setScroll}
                                    scrollLeftRight={scrollLeftRight}
                                    submissionType={submissionType}
                                    submissionEnum={submissionEnum}
                                    scrollableRef={scrollableRef}
                                    scrollablePerDiem={scrollablePerDiem}
                                />
                            )}
                        {([
                            timesheetsStatuses(t).rejected.apiStatus,
                            timesheetsStatuses(t).draft.apiStatus,
                            timesheetsStatuses(t).approved.apiStatus,
                            timesheetsStatuses(t).processed.apiStatus,
                            timesheetsStatuses(t).submitted.apiStatus
                        ].includes(section?.Status?.Status) ||
                            (timesheetInfo.isApprover && section.Status.Status === timesheetsStatuses(t).submitted.apiStatus)) && (
                                <ApproverComment
                                    key={new Date()}
                                    comments={(timesheet?.IsSplitApprovalFlow ? section.TimesheetRejectionReasons : timesheet?.TimesheetRejectionReasons)?.sort((a, b) => (a.Id > b.Id ? -1 : 1))}
                                    disabled={
                                        !timesheetInfo.isApprover ||
                                        [
                                            timesheetsStatuses(t).approved.apiStatus,
                                            timesheetsStatuses(t).processed.apiStatus,
                                            timesheetsStatuses(t).rejected.apiStatus
                                        ].includes(section.Status.Status)
                                    }
                                    isApprover={timesheetInfo.isApprover}
                                    extraClass="py-6"
                                    saveComment={saveCommentHandler}
                                    deleteComment={deleteCommentHandler}
                                    commentSaving={commentsaving}
                                />
                            )}
                    </div>
                </AccordionDetails>
            </Accordion>
            {openPerDiemError && (
                <NotificationModal open={openPerDiemError} onClose={() => setOpenPerDiemError('')}>
                    <p className={classes.errorMessage}>{openPerDiemError}</p>
                </NotificationModal>
            )}
        </div>
    )
};

export default TimesheetSections;